/* eslint-disable import/prefer-default-export */
import mime from 'mime-types';
import { DateTime } from 'luxon';

import compact from 'lodash.compact';
import concat from 'lodash.concat';
import cloneDeepWith from 'lodash.clonedeepwith';
import defaultTo from 'lodash.defaultto';
import flatten from 'lodash.flatten';
import get from 'lodash.get';
import includes from 'lodash.includes';
import last from 'lodash.last';
import isNil from 'lodash.isnil';
import omitBy from 'lodash.omitby';
import pick from 'lodash.pick';
import transform from 'lodash.transform';
import uniq from 'lodash.uniq';

import Api from './api';

import {
  consignmentFloats,
  consignmentIntegers,
  consignmentIsoStrings,
  consignmentItemFloats,
  consignmentItemIntegers,
  consignmentItemIsoStrings,
  consignmentImageFloats,
  consignmentImageIntegers,
  consignmentImageIsoStrings,
  inverterFloats,
  inverterIntegers,
  inverterIsoStrings,
  picklistFloats,
  picklistIntegers,
  picklistIsoStrings,
  picklistProductFloats,
  picklistProductIntegers,
  picklistProductIsoStrings,
  picklistSupplierCatalogItemFloats,
  picklistSupplierCatalogItemIntegers,
  picklistSupplierCatalogItemIsoStrings,
  picklistTemplateFloats,
  picklistTemplateIntegers,
  picklistTemplateIsoStrings,
  picklistTemplateProductFloats,
  picklistTemplateProductIntegers,
  picklistTemplateProductIsoStrings,
  pileFloats,
  pileIntegers,
  pileIsoStrings,
  pilingMachineFloats,
  pilingMachineIntegers,
  pilingMachineIsoStrings,
  productFloats,
  productIntegers,
  productIsoStrings,
  productProductFloats,
  productProductIntegers,
  productProductIsoStrings,
  productSupplierCatalogItemFloats,
  productSupplierCatalogItemIntegers,
  productSupplierCatalogItemIsoStrings,
  receiptFloats,
  receiptIntegers,
  receiptIsoStrings,
  siteFloats,
  siteIntegers,
  siteIsoStrings,
  siteLocationFloats,
  siteLocationIntegers,
  siteLocationIsoStrings,
  tableFloats,
  tableIntegers,
  tableIsoStrings,
  userFloats,
  userIntegers,
  userIsoStrings,
  supplierFloats,
  supplierIntegers,
  supplierIsoStrings,
  supplierCatalogFloats,
  supplierCatalogIntegers,
  supplierCatalogIsoStrings,
  supplierCatalogItemFloats,
  supplierCatalogItemIntegers,
  supplierCatalogItemIsoStrings,
  productCategoryFloats,
  productCategoryIntegers,
  productCategoryIsoStrings,
  manufacturerFloats,
  manufacturerIntegers,
  manufacturerIsoStrings,
  purchaserFloats,
  purchaserIntegers,
  purchaserIsoStrings,
  wbsCodeFloats,
  wbsCodeIntegers,
  wbsCodeIsoStrings,
} from '../white_lists';

const integers = uniq(
  compact(
    flatten(
      concat(
        [],
        consignmentIntegers,
        consignmentItemIntegers,
        consignmentImageIntegers,
        inverterIntegers,
        picklistIntegers,
        picklistProductIntegers,
        picklistSupplierCatalogItemIntegers,
        picklistTemplateIntegers,
        picklistTemplateProductIntegers,
        pileIntegers,
        pilingMachineIntegers,
        productIntegers,
        productProductIntegers,
        productSupplierCatalogItemIntegers,
        receiptIntegers,
        siteIntegers,
        siteLocationIntegers,
        tableIntegers,
        userIntegers,
        supplierIntegers,
        supplierCatalogIntegers,
        supplierCatalogItemIntegers,
        productCategoryIntegers,
        purchaserIntegers,
        manufacturerIntegers,
        wbsCodeIntegers
      )
    )
  )
);

const floats = uniq(
  compact(
    flatten(
      concat(
        [],
        consignmentFloats,
        consignmentItemFloats,
        consignmentImageFloats,
        inverterFloats,
        picklistFloats,
        picklistProductFloats,
        picklistSupplierCatalogItemFloats,
        picklistTemplateFloats,
        picklistTemplateProductFloats,
        pileFloats,
        pilingMachineFloats,
        productFloats,
        productProductFloats,
        productSupplierCatalogItemFloats,
        receiptFloats,
        siteFloats,
        siteLocationFloats,
        tableFloats,
        userFloats,
        supplierFloats,
        supplierCatalogFloats,
        supplierCatalogItemFloats,
        productCategoryFloats,
        purchaserFloats,
        manufacturerFloats,
        wbsCodeFloats
      )
    )
  )
);

const isoStrings = uniq(
  compact(
    flatten(
      concat(
        [],
        consignmentIsoStrings,
        consignmentItemIsoStrings,
        consignmentImageIsoStrings,
        inverterIsoStrings,
        manufacturerIsoStrings,
        picklistIsoStrings,
        picklistProductIsoStrings,
        picklistSupplierCatalogItemIsoStrings,
        picklistTemplateIsoStrings,
        picklistTemplateProductIsoStrings,
        pileIsoStrings,
        pilingMachineIsoStrings,
        productIsoStrings,
        productProductIsoStrings,
        productSupplierCatalogItemIsoStrings,
        receiptIsoStrings,
        siteIsoStrings,
        siteLocationIsoStrings,
        supplierIsoStrings,
        supplierCatalogIsoStrings,
        supplierCatalogItemIsoStrings,
        productCategoryIsoStrings,
        purchaserIsoStrings,
        tableIsoStrings,
        userIsoStrings,
        wbsCodeIsoStrings
      )
    )
  )
);

function omitByNil(data) {
  return omitBy(data, isNil);
}

export function pickValues(data, whitelist = ['id']) {
  return pick(omitByNil(data), whitelist);
}

function inputTyper(value, key) {
  if (key && !(typeof value === 'object')) {
    if (includes(floats, key)) {
      return defaultTo(parseFloat(value), null);
    }
    if (includes(integers, key)) {
      return defaultTo(parseInt(value, 10), null);
    }
    if (includes(isoStrings, key)) {
      try {
        return DateTime.fromISO(value).toUTC().toISO();
      } catch {
        return null;
      }
    }
    if (value === '') {
      return null;
    }
  }
  return undefined;
}

export function coerceInput(data) {
  const coercedData = cloneDeepWith(data, inputTyper);
  return coercedData;
}

export function getExport(exportPath, displayName, args = {}) {
  const exportType = last(exportPath.split('.'));
  const contentType = mime.lookup(exportType);
  return Api.get(`/export/${exportPath}`, args, {
    responseType: 'arraybuffer',
  }).then((resp) => {
    const blob = new Blob([resp.data], { type: contentType });
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = window.URL.createObjectURL(blob);
    link.download = displayName || exportPath;
    link.click();
  });
}

const transformSubmitErrors = (errors) =>
  transform(
    errors,
    (result, values, key) => {
      /* eslint-disable no-param-reassign */
      if (Array.isArray(values) && values.length > 0) {
        if (typeof get(values, [0, 'message']) === 'string') {
          result[key] = compact(values.map((value) => value.message));
        } else {
          result[key] = values.map((value) => transformSubmitErrors(value));
        }
      }
    },
    {}
  );

export function handleSubmitError(err) {
  const errorMessage = get(err, ['graphQLErrors', '0', 'message']);
  let submitErrors = {};
  const graphqlErrors = get(
    err,
    ['graphQLErrors', '0', 'extensions', 'data', 'errors'],
    {}
  );
  if (Object.keys(graphqlErrors).length > 0) {
    submitErrors = transformSubmitErrors(graphqlErrors);
  }
  return {
    errorMessage: errorMessage || 'Something went wrong',
    submitErrors: Object.keys(submitErrors).length > 0 ? submitErrors : undefined,
  };
}

export const arrayWithItemAndKey = (array) =>
  array.map((item, index) => ({
    key: `item-${index}`,
    item,
  }));

export function renderMultlilineText(text) {
  if (text) {
    return text.split('\n').map((line, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={`notes-${index}`}>
        {line}
        <br />
      </span>
    ));
  }
  return undefined;
}
